import ProcessVersionHistory from '@components/ProcessVersionHistory';
import WolfStepIndicator from '@components/ui/WolfStepIndicator';
import { graphQlClient } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import { useLoader } from '@hooks/useLoader';
import { useProcessNames } from '@hooks/useProcessNames';
import { useTranslation } from '@hooks/useTranslation';
import { getProcessByIdWithDiscrepanciesRequest, Process } from '@models/process.model';
import { Step, StepType } from '@models/step.model';
import { Box, Typography } from '@mui/material';
import enhanceHTML from '@utils/enhanceHtml';
import { filterStepsInsideSwitchRepeat } from '@utils/filterStepsInsideSwitchRepeat';
import { getStepsInsideSwitchRepeat } from '@utils/getStepsInsideSwitchRepeat';
import sanitizeHTML from '@utils/sanitize-html';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const ProcessPrint: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const { setLoading } = useLoader();
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');

  const [currentProcess, setCurrentProcess] = useState<Process>();
  const [enhancedHtmlMap, setEnhancedHtmlMap] = useState<{ [key: string]: { __html: string } }>({});
  const [notFound, setNotFound] = useState(false);

  // Extract process IDs for the hook
  const processIds = useMemo(() => {
    return currentProcess?.steps?.map((step) => step?.stepData?.executeProcessId).filter(Boolean) || [];
  }, [currentProcess?.steps]);

  // Use the optimized hook to get process names
  const { getProcessNameById } = useProcessNames(processIds);

  useEffect(() => {
    getProcessData(processId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  useEffect(() => {
    const fetchAndEnhanceHTML = async () => {
      const enhancedHtmls: { [key: string]: { __html: string } } = {};
      if (currentProcess?.steps && currentProcess?.steps.length > 0) {
        const auxSteps = [...currentProcess.steps];
        for (const step of auxSteps) {
          const sanitizedHtml = sanitizeHTML(step?.stepData?.description);
          const enhanced = await enhanceHTML(sanitizedHtml);
          enhancedHtmls[step.stepId] = enhanced;
        }
        setEnhancedHtmlMap(enhancedHtmls);
      }
    };

    if (currentProcess?.steps && currentProcess?.steps.length > 0) {
      fetchAndEnhanceHTML();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcess?.steps]);

  const getProcessData = async (id: string) => {
    try {
      if (!id) return;
      const variables = { id };
      const data: getProcessByIdWithDiscrepanciesRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessByIdWithDiscrepancies,
        variables,
      );
      if (data?.getProcessByIdWithDiscrepancies) {
        setCurrentProcess(data?.getProcessByIdWithDiscrepancies);
      }
    } catch (e: any) {
      console.log('error', e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const renderSidebarStep = (
    step: Step,
    index: number,
    isSubstep: boolean = false,
    parentIndex?: number,
  ): JSX.Element => {
    const stepNumber =
      isSubstep && parentIndex !== undefined
        ? step.stepType === StepType.SWITCH
          ? `${parentIndex + 1}.${String.fromCharCode(index + 65)}`
          : `${parentIndex + 1}.${index + 1}`
        : `${index + 1}`;

    return (
      <Box key={step.stepId} className={isSubstep ? 'ml-4' : ''}>
        <Typography variant="body14" className="mb-1">
          <span className="font-semibold">{stepNumber}</span> {step.name}
        </Typography>
        {[StepType.REPEAT, StepType.SWITCH].includes(step.stepType) &&
          getStepsInsideSwitchRepeat(step?.stepData?.stepsIds, currentProcess?.steps).map((substep, idx) =>
            renderSidebarStep(substep, idx, true, index),
          )}
      </Box>
    );
  };

  const renderStep = (
    step: Step,
    index: number,
    isSubstep: boolean = false,
    parentIndex?: number,
    parentType?: StepType,
  ) => (
    <Box key={step.stepId} className={`mb-6 ${isSubstep ? 'ml-1 border-l-2 border-gray-200 mt-4' : ''}`}>
      <Box className="flex gap-2 items-start mb-2">
        <WolfStepIndicator
          text={
            isSubstep && parentIndex !== undefined
              ? step.stepType === StepType.SWITCH
                ? parentIndex + 1 + '.' + String.fromCharCode(index + 65)
                : parentIndex + 1 + '.' + (index + 1)
              : (index + 1).toString()
          }
          type={isSubstep && parentType ? parentType : step.stepType}
          isSub={isSubstep}
        />
        <Box className="flex-1">
          <Typography variant="h5" className="font-semibold mb-2">
            {step?.name}
          </Typography>

          {step?.notes && (
            <Box className="p-3 mb-3 border border-solid border-gray-300 rounded bg-yellow-50">
              <Typography variant="body12semibold" className="block mb-1">
                {localeProcesses['notes']}
              </Typography>
              <Typography variant="body14">{step.notes}</Typography>
            </Box>
          )}

          {step?.stepData?.condition && (
            <Box className="p-3 mb-3 border border-solid border-red-200 rounded bg-red-50">
              <Typography variant="body12semibold" className="block mb-1">
                {localeProcesses['condition']}
              </Typography>
              <Typography variant="body14">{step.stepData.condition}</Typography>
            </Box>
          )}

          {step?.stepData?.description && (
            <div className="reset-base-styles mb-3" dangerouslySetInnerHTML={enhancedHtmlMap[step?.stepId]} />
          )}

          {step?.stepData?.executeProcessId && (
            <Box className="p-3 mb-3 border border-solid border-blue-200 rounded bg-blue-50">
              <Typography variant="body12" className="text-gray-600">
                {localeProcesses['linkedToProcess']}:{' '}
                {getProcessNameById(step.stepData.executeProcessId) || step.stepData.executeProcessId}
              </Typography>
            </Box>
          )}

          {/* Render substeps for REPEAT and SWITCH */}
          {[StepType.REPEAT, StepType.SWITCH].includes(step.stepType) &&
            getStepsInsideSwitchRepeat(step?.stepData?.stepsIds, currentProcess?.steps).map((substep, idx) =>
              renderStep(substep, idx, true, index, step.stepType),
            )}
        </Box>
      </Box>
    </Box>
  );

  if (notFound) {
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Box className="text-center">
          <Typography variant="h4" className="mb-2">
            {localeProcesses['processNotFound']}
          </Typography>
          <Typography variant="body1" className="text-gray-600">
            {localeProcesses['processNotFoundMessage']}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!currentProcess) {
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Typography variant="h5">{localeCommon['loading']}</Typography>
      </Box>
    );
  }

  return (
    <Box className="print-preview-container bg-white min-h-screen p-8">
      {/* Header Section - Full Width */}
      <Box className="mb-8 pb-4 border-b-2 border-gray-300">
        <Typography variant="h2" className="font-bold !mb-4">
          {currentProcess.name}
        </Typography>

        {currentProcess.goal && (
          <Box className="mb-3">
            <Typography variant="body14semibold" className="text-gray-900 font-bold">
              {localeCommon['goal']}:
            </Typography>
            <Typography variant="body16" className="mt-1">
              {currentProcess.goal}
            </Typography>
          </Box>
        )}

        {currentProcess.category && (
          <Box className="mb-3">
            <Typography variant="body14semibold" className="text-gray-900 font-bold">
              {localeCommon['category']}:
            </Typography>
            <Typography variant="body16" className="mt-1">
              {currentProcess.category}
            </Typography>
          </Box>
        )}

        {currentProcess.service && (
          <Box className="mb-3">
            <Box className="flex items-baseline gap-1">
              {currentProcess.service.area?.name && (
                <>
                  <Typography variant="body14semibold" className="text-gray-900 font-bold">
                    {localeCommon['area']}:
                  </Typography>
                  <Typography variant="body16">{currentProcess.service.area.name}</Typography>
                  <Typography variant="body14semibold" className="text-gray-900 font-bold">
                    |
                  </Typography>
                </>
              )}
              <Typography variant="body14semibold" className="text-gray-900 font-bold">
                {localeCommon['service']}:
              </Typography>
              <Typography variant="body16">{currentProcess.service.name}</Typography>
            </Box>
          </Box>
        )}

        {currentProcess.location && (
          <Box className="mb-3">
            <Typography variant="body14semibold" className="text-gray-900 font-bold">
              Location:
            </Typography>
            <Typography variant="body16" className="mt-1">
              {currentProcess.location}
            </Typography>
          </Box>
        )}

        <Box className="mb-3 flex gap-6">
          {currentProcess.currentVersion && (
            <Box>
              <Typography variant="body14semibold" className="text-gray-900 font-bold">
                {localeProcesses['currentVersion']}:
              </Typography>
              <Typography variant="body16" className="mt-1">
                v{currentProcess.currentVersion}
              </Typography>
            </Box>
          )}
          {currentProcess.lastUpdatedAt && (
            <Box>
              <Typography variant="body14semibold" className="text-gray-900 font-bold">
                {localeProcesses['updatedOn']}:
              </Typography>
              <Typography variant="body16" className="mt-1">
                {DateTime.fromISO(currentProcess.lastUpdatedAt).toFormat('dd-MM-yyyy HH:mm')}
              </Typography>
            </Box>
          )}
        </Box>

        {currentProcess.policies && currentProcess.policies.length > 0 && (
          <Box className="mb-3">
            <Typography variant="body14semibold" className="text-gray-900 font-bold">
              {localeCommon['policies']}:
            </Typography>
            <Box className="mt-1">
              {currentProcess.policies.map((policy, idx) => (
                <Typography key={policy.policyId} variant="body16">
                  {idx + 1}. {policy.name}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {currentProcess.triggers && currentProcess.triggers.length > 0 && (
          <Box className="mb-3">
            <Typography variant="body14semibold" className="text-gray-900 font-bold">
              {localeCommon['triggers']}:
            </Typography>
            <Box className="mt-1">
              {currentProcess.triggers.map((trigger, idx) => (
                <Typography key={trigger.triggerId} variant="body16">
                  {idx + 1}. {trigger.name} ({trigger.triggerType.toLowerCase()})
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Outline Section */}
      <Box className="mb-8">
        <Typography variant="h4" className="font-semibold mb-4">
          Outline
        </Typography>
        <Box className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          {currentProcess.steps &&
            filterStepsInsideSwitchRepeat(currentProcess.steps)
              .sort((a, b) => a.order - b.order)
              .map((step, index) => renderSidebarStep(step, index))}
        </Box>
      </Box>

      {/* Steps Section */}
      <Box className="steps-section">
        {currentProcess.steps && currentProcess.steps.length > 0 ? (
          <Box>
            {filterStepsInsideSwitchRepeat(currentProcess.steps)
              .sort((a, b) => a.order - b.order)
              .map((step, index) => renderStep(step, index))}
          </Box>
        ) : (
          <Typography variant="body16" className="text-gray-600">
            {localeProcesses['noSteps']}
          </Typography>
        )}
      </Box>

      {/* Version History Section */}
      <Box className="mb-8">
        <ProcessVersionHistory
          processId={processId || ''}
          currentVersion={currentProcess.currentVersion}
          lastUpdatedAt={currentProcess.lastUpdatedAt}
        />
      </Box>

      {/* Print Button - Only visible on screen, hidden when printing */}
      <Box className="fixed bottom-8 right-8 no-print">
        <button
          onClick={() => window.print()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-colors">
          {localeProcesses['print']}
        </button>
      </Box>
    </Box>
  );
};

export default ProcessPrint;
